import moment from 'moment';
import { TicketsParsedDataItem } from '../tickets/TicketsInterfaces';
import { UserMetricsParsedData } from '../user-metrics/UserMetricsInterfaces';
import {
  getFinOpsAllowedMonths,
  objectToOptionsForMonths,
  OptionItem,
  convertToChartFormat,
  calculateChartDomains as calculateChartDomainsShared,
  getMostRecentPeriod
} from '../FinTechOpsDataProcessingFunctions';
import { FilterDateFormat_MMM_YYYY, ALL_APPLICATIONS, SKYNET_ALL } from '../FinTechOpsConstants';
import { DURATION_OPTIONS } from './BusinessReportingConstants';
import {
  getTotalApplicationViewByTeam,
  getTop5ApplicationsByMonth,
  getTotalUniqueUsersByApplicationAndMonth,
  getTotalViewsByApplicationType
} from '../user-metrics/UserMetricsDataProcessing';

import { FilterableItem, FilterConfig } from './BusinessReportingSelectionFilters';
import { logger } from 'src/logger';

interface FilterData {
  applications: string[];
  periods: string[];
}

// Extend the existing FilterConfig to add logContext
type MasterFilterConfig<T> = FilterConfig<T> & {
  logContext: string;
};

/**
 * Gets applications that exist in both tickets and user metrics data (intersection)
 * Excludes system applications and sub-applications
 */
export const getBusinessReportingApplications = (ticketsData: TicketsParsedDataItem[], userMetricsData: UserMetricsParsedData[]): string[] => {
  // Get applications from tickets (using assignees field which contains application data)
  const ticketApplications = ticketsData.map((ticket) => ticket.application_name).filter((app) => app);
  const userMetricApplications = userMetricsData.map((userMetric) => userMetric.application_type).filter((app) => app);

  const uniqueTicketApps = [...new Set(ticketApplications)];
  const uniqueUserMetricApps = [...new Set(userMetricApplications)];

  // Find intersection and exclude system entries
  const systemEntries = [ALL_APPLICATIONS, SKYNET_ALL, 'common'];

  return uniqueTicketApps.filter((app) => uniqueUserMetricApps.includes(app) && !systemEntries.includes(app) && app.trim() !== '').sort();
};

/**
 * Groups months by year and returns the structure: { '2024': [1, 2, 3], '2023': [10, 11, 12] }
 */
const groupMonthsByYear = (months: string[]): Record<string, number[]> => {
  const monthsByYear: Record<string, number[]> = {};

  months.forEach((monthStr) => {
    const momentObj = moment(monthStr, FilterDateFormat_MMM_YYYY);
    const year = momentObj.year().toString();
    const month = momentObj.month() + 1; // Convert to 1-based month number

    if (!monthsByYear[year]) {
      monthsByYear[year] = [];
    }
    monthsByYear[year].push(month);
  });

  return monthsByYear;
};

/**
 * Gets complete quarters from available months within the same calendar year
 * Returns quarters in format: ['Q1 2024', 'Q2 2024', etc.]
 */
export const getCompleteQuartersFromMonths = (months: string[]): OptionItem[] => {
  const quarterMap = {
    Q1: [1, 2, 3], // Jan, Feb, Mar
    Q2: [4, 5, 6], // Apr, May, Jun
    Q3: [7, 8, 9], // Jul, Aug, Sep
    Q4: [10, 11, 12] // Oct, Nov, Dec
  };

  const availableQuarters: string[] = [];
  const monthsByYear = groupMonthsByYear(months);

  // Check each year separately
  Object.entries(monthsByYear).forEach(([year, yearMonths]) => {
    Object.entries(quarterMap).forEach(([quarter, requiredMonths]) => {
      const hasCompleteQuarter = requiredMonths.every((month) => yearMonths.includes(month));
      if (hasCompleteQuarter) {
        availableQuarters.push(`${quarter} ${year}`);
      }
    });
  });

  // Sort quarters chronologically (newest first)
  availableQuarters.sort((a, b) => {
    const [quarterA, yearA] = a.split(' ');
    const [quarterB, yearB] = b.split(' ');

    // First sort by year (descending)
    if (yearA !== yearB) {
      return parseInt(yearB) - parseInt(yearA);
    }

    // Then by quarter (descending)
    const quarterOrder = { Q4: 4, Q3: 3, Q2: 2, Q1: 1 };
    return quarterOrder[quarterB as keyof typeof quarterOrder] - quarterOrder[quarterA as keyof typeof quarterOrder];
  });

  return availableQuarters.map((quarter) => ({
    label: quarter,
    value: quarter
  }));
};

/**
 * Gets monthly options using existing utility functions
 */
export const getMonthlyOptions = (): OptionItem[] => {
  const months = getFinOpsAllowedMonths(FilterDateFormat_MMM_YYYY);
  return objectToOptionsForMonths(months);
};

/**
 * Gets quarterly options from available months
 */
export const getQuarterlyOptions = (): OptionItem[] => {
  const months = getFinOpsAllowedMonths(FilterDateFormat_MMM_YYYY);
  return getCompleteQuartersFromMonths(months);
};

/**
 * Converts quarter selection to individual months for filtering
 * E.g., 'Q1 2024' -> ['Jan-2024', 'Feb-2024', 'Mar-2024']
 * Uses FilterDateFormat_MMM_YYYY format (MMM-YYYY) to match ticket data
 */
export const convertQuarterToMonths = (quarterSelection: string): string[] => {
  // Validate input format
  if (!quarterSelection || !/^Q[1-4] \d{4}$/.test(quarterSelection)) {
    return [];
  }
  const [quarter, year] = quarterSelection.split(' ');

  const quarterMonths = {
    Q1: ['Jan', 'Feb', 'Mar'],
    Q2: ['Apr', 'May', 'Jun'],
    Q3: ['Jul', 'Aug', 'Sep'],
    Q4: ['Oct', 'Nov', 'Dec']
  };

  const months = quarterMonths[quarter as keyof typeof quarterMonths] || [];
  // Use dash format to match FilterDateFormat_MMM_YYYY (MMM-YYYY)
  return months.map((month) => `${month}-${year}`);
};

/**
 * Gets default selections for all filters
 */
export const getDefaultSelections = (applications: string[], rollupPeriod: string = DURATION_OPTIONS.MONTHLY) => {
  const periods = rollupPeriod === DURATION_OPTIONS.QUARTERLY ? getQuarterlyOptions() : getMonthlyOptions();

  return {
    applications: applications.map((app) => ({ label: app, value: app })), // All applications selected
    rollupPeriod: rollupPeriod,
    period: periods // All periods (months/quarters) selected by default
  };
};

/**
 * Validates if the provided periods are valid based on rollup type
 */
export const validatePeriodSelection = (periods: OptionItem[], rollupType: string): boolean => {
  if (periods.length === 0) return false;

  if (rollupType === DURATION_OPTIONS.QUARTERLY) {
    // Validate quarter format: 'Q1 2024', etc.
    return periods.every((period) => /^Q[1-4] \d{4}$/.test(period.value));
  } else {
    // Validate month format: 'Jan 2024', etc.
    return periods.every((period) => moment(period.value, FilterDateFormat_MMM_YYYY, true).isValid());
  }
};

/**
 * Interface for chart data processing
 */
export interface UserMetricsDataForGraphProcessing {
  title: string;
  category: string;
  count: number;
}

/**
 * Business Reporting version of Total Application Views by Team
 */
export const getTotalApplicationViewByTeamForBusinessReporting = (
  filteredUserMetricsData: UserMetricsParsedData[]
): UserMetricsDataForGraphProcessing[] => {
  // undefined filters because data is already filtered
  return getTotalApplicationViewByTeam(filteredUserMetricsData, undefined, true);
};

/**
 * Business Reporting version of Top 5 Sub Application - Unique Users
 */
export const getTop5ApplicationsByMonthForBusinessReporting = (
  filteredUserMetricsData: UserMetricsParsedData[]
): UserMetricsDataForGraphProcessing[] => {
  // undefined filters because data is already filtered
  return getTop5ApplicationsByMonth(filteredUserMetricsData, undefined, true);
};

/**
 * Business Reporting version of Total Unique Users by Application
 */
export const getTotalUniqueUsersByApplicationForBusinessReporting = (
  filteredUserMetricsData: UserMetricsParsedData[]
): UserMetricsDataForGraphProcessing[] => {
  // undefined filters because data is already filtered
  return getTotalUniqueUsersByApplicationAndMonth(filteredUserMetricsData, true);
};

/**
 * Business Reporting version of Total Views by Application
 */
export const getTotalViewsByApplicationForBusinessReporting = (
  filteredUserMetricsData: UserMetricsParsedData[]
): UserMetricsDataForGraphProcessing[] => {
  // undefined filters because data is already filtered
  return getTotalViewsByApplicationType(filteredUserMetricsData, true);
};

/**
 * Converts chart data to the format required by BarLineChartWithProps
 */
export const convertDataToChartFormat = (data: UserMetricsDataForGraphProcessing[], chartType: 'bar' | 'line' = 'bar') => {
  return convertToChartFormat(data, 'title', 'category', 'count', chartType);
};

/**
 * Calculate domains for charts
 */
export const calculateChartDomains = (data: UserMetricsDataForGraphProcessing[]) => {
  return calculateChartDomainsShared(data, 'category', 'count');
};
export { getMostRecentPeriod, type FilterData };

/**
 * Generic function to create master filter data for business reporting
 */
export const createBusinessReportingMasterFilterData = <T extends FilterableItem>(data: T[], config: MasterFilterConfig<T>): FilterData => {
  try {
    const uniqueApplications = new Set<string>();
    const uniquePeriods = new Set<string>();

    data.forEach((item) => {
      const applicationValue = config.getApplicationField(item);
      const dateValue = config.getDateField(item);

      if (applicationValue) uniqueApplications.add(applicationValue);
      if (dateValue) {
        if (config.dateFormat) {
          const momentObj = moment(dateValue);
          if (momentObj.isValid()) {
            const formattedDate = momentObj.format(config.dateFormat);
            uniquePeriods.add(formattedDate);
          }
        } else {
          uniquePeriods.add(dateValue);
        }
      }
    });

    const filterData: FilterData = {
      applications: Array.from(uniqueApplications).sort(),
      periods: Array.from(uniquePeriods).sort()
    };

    logger.debug(`Created master filter data for ${config.logContext}`, {
      applicationCount: filterData.applications.length,
      periodCount: filterData.periods.length
    });

    return filterData;
  } catch (error) {
    logger.error(`Error creating master filter data for ${config.logContext}`, error as Error);
    return {
      applications: [],
      periods: []
    };
  }
};
